import { useState, useEffect, useCallback } from 'react';
import { getAudienceSocket } from '../hooks/useAudienceState';
import { BattleEffectsOverlay } from '../../display/components/BattleEffectsOverlay';

interface BattleCombatant {
  id: string;
  name: string;
  damage: number;
  maxHP: number;
  currentHP: number;
  type: 'hero' | 'enemy' | 'ally';
  teamColor?: string;
  characterId?: string;
}

interface BattleState {
  id: string;
  sessionId: string;
  combatants: BattleCombatant[];
  isActive: boolean;
}

interface BattleScreenProps {
  battleState: BattleState;
}

interface FloatingText {
  targetId: string;
  value: number;
  isHeal: boolean;
  key: number;
  animate: boolean;
}

interface DeathSkull {
  targetId: string;
  key: number;
  animate: boolean;
}

const TEAM_COLORS: Record<string, string> = {
  BLUE: '#3b82f6',
  RED: '#ef4444',
  GREEN: '#22c55e',
  YELLOW: '#eab308',
};

const ALLY_COLOR = '#60a5fa';
const ENEMY_COLOR = '#a78bfa';

function getBarColor(combatant: BattleCombatant): string {
  if (combatant.type === 'enemy') return ENEMY_COLOR;
  if (combatant.type === 'ally') return ALLY_COLOR;
  if (combatant.teamColor && TEAM_COLORS[combatant.teamColor]) {
    return TEAM_COLORS[combatant.teamColor];
  }
  return ALLY_COLOR;
}

const SKULL_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="white"><path d="M32 4C18.7 4 8 14.7 8 28c0 8.4 4.3 15.8 10.8 20.1V56c0 2.2 1.8 4 4 4h18.4c2.2 0 4-1.8 4-4v-7.9C51.7 43.8 56 36.4 56 28 56 14.7 45.3 4 32 4zm-8 32a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm16 0a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm-4 10h-8v-4h8v4z"/></svg>`;

type BattleOutcome = 'victory' | 'defeat' | 'draw' | null;

export default function BattleScreen({ battleState }: BattleScreenProps) {
  const [combatants, setCombatants] = useState<BattleCombatant[]>(battleState.combatants);
  const [shakingIds, setShakingIds] = useState<Set<string>>(new Set());
  const [glowingIds, setGlowingIds] = useState<Set<string>>(new Set());
  const [floatingTexts, setFloatingTexts] = useState<FloatingText[]>([]);
  const [deathSkulls, setDeathSkulls] = useState<DeathSkull[]>([]);
  const [outcome, setOutcome] = useState<BattleOutcome>(null);
  const [outcomeVisible, setOutcomeVisible] = useState(false);

  const [prevHPMap, setPrevHPMap] = useState<Map<string, number>>(() => {
    const m = new Map<string, number>();
    battleState.combatants.forEach((c) => m.set(c.id, c.currentHP));
    return m;
  });

  useEffect(() => {
    const socket = getAudienceSocket();
    if (!socket) return;

    const handleDamage = (data: { battleId: string; targetId: string; damage: number; newHP: number; maxHP: number }) => {
      setShakingIds((prev) => new Set(prev).add(data.targetId));
      setTimeout(() => {
        setShakingIds((prev) => { const next = new Set(prev); next.delete(data.targetId); return next; });
      }, 500);

      const key = Date.now() + Math.random();
      setFloatingTexts((prev) => [...prev, { targetId: data.targetId, value: data.damage, isHeal: false, key, animate: false }]);
      requestAnimationFrame(() => {
        setFloatingTexts((prev) => prev.map((d) => (d.key === key ? { ...d, animate: true } : d)));
      });
      setTimeout(() => {
        setFloatingTexts((prev) => prev.filter((d) => d.key !== key));
      }, 1100);

      // Death skull (delayed 2s to not overlap with damage number)
      const prevHP = prevHPMap.get(data.targetId) ?? data.maxHP;
      if (data.newHP <= 0 && prevHP > 0) {
        const skullKey = Date.now() + Math.random();
        setTimeout(() => {
          setDeathSkulls((prev) => [...prev, { targetId: data.targetId, key: skullKey, animate: false }]);
          requestAnimationFrame(() => {
            setDeathSkulls((prev) => prev.map((s) => (s.key === skullKey ? { ...s, animate: true } : s)));
          });
          setTimeout(() => {
            setDeathSkulls((prev) => prev.filter((s) => s.key !== skullKey));
          }, 1500);
        }, 800);
      }
      setPrevHPMap((prev) => new Map(prev).set(data.targetId, data.newHP));
    };

    const handleHeal = (data: { battleId: string; targetId: string; amount: number; newHP: number; maxHP: number }) => {
      setGlowingIds((prev) => new Set(prev).add(data.targetId));
      setTimeout(() => {
        setGlowingIds((prev) => { const next = new Set(prev); next.delete(data.targetId); return next; });
      }, 800);

      const key = Date.now() + Math.random();
      setFloatingTexts((prev) => [...prev, { targetId: data.targetId, value: data.amount, isHeal: true, key, animate: false }]);
      requestAnimationFrame(() => {
        setFloatingTexts((prev) => prev.map((d) => (d.key === key ? { ...d, animate: true } : d)));
      });
      setTimeout(() => {
        setFloatingTexts((prev) => prev.filter((d) => d.key !== key));
      }, 1100);

      setPrevHPMap((prev) => new Map(prev).set(data.targetId, data.newHP));
    };

    const handleStateUpdate = (data: { battleId: string; combatants: BattleCombatant[] }) => {
      setCombatants(data.combatants);
    };

    const handleOutcome = (data: { battleId: string; outcome: 'victory' | 'defeat' | 'draw' }) => {
      setOutcome(data.outcome);
      requestAnimationFrame(() => setOutcomeVisible(true));
    };

    socket.on('battle:damage', handleDamage);
    socket.on('battle:heal', handleHeal);
    socket.on('battle:state-update', handleStateUpdate);
    socket.on('battle:outcome', handleOutcome);

    return () => {
      socket.off('battle:damage', handleDamage);
      socket.off('battle:heal', handleHeal);
      socket.off('battle:state-update', handleStateUpdate);
      socket.off('battle:outcome', handleOutcome);
    };
  }, [prevHPMap]);

  const heroes = combatants.filter((c) => c.type === 'hero' || c.type === 'ally');
  const enemies = combatants.filter((c) => c.type === 'enemy');

  const renderCombatant = useCallback(
    (combatant: BattleCombatant) => {
      const hpPercent = combatant.maxHP > 0 ? (combatant.currentHP / combatant.maxHP) * 100 : 0;
      const isShaking = shakingIds.has(combatant.id);
      const isGlowing = glowingIds.has(combatant.id);
      const isDead = combatant.currentHP <= 0;
      const barColor = getBarColor(combatant);
      const texts = floatingTexts.filter((d) => d.targetId === combatant.id);
      const skulls = deathSkulls.filter((s) => s.targetId === combatant.id);

      return (
        <div
          key={combatant.id}
          className={`relative mb-3 ${isShaking ? 'battle-shake' : ''} ${isGlowing ? 'battle-glow' : ''} ${isDead ? 'opacity-40' : ''}`}
          style={{ transition: 'opacity 0.5s ease' }}
        >
          {/* Floating damage/heal numbers */}
          {texts.map((d) => (
            <span
              key={d.key}
              className={`absolute left-1/2 -translate-x-1/2 font-bold pointer-events-none z-10 ${d.isHeal ? 'text-green-400' : 'text-red-500'}`}
              style={{
                fontSize: '1.5rem',
                top: d.animate ? '-24px' : '4px',
                opacity: d.animate ? 0 : 1,
                transition: 'top 1s ease-out, opacity 1s ease-out',
                textShadow: '0 0 6px rgba(0,0,0,0.8)',
              }}
            >
              {d.isHeal ? '+' : '-'}{d.value}
            </span>
          ))}

          {/* Death skull */}
          {skulls.map((s) => (
            <div
              key={s.key}
              className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
            >
              <div
                style={{
                  width: s.animate ? '56px' : '14px',
                  height: s.animate ? '56px' : '14px',
                  opacity: s.animate ? 0 : 1,
                  transition: 'width 1s ease-out, height 1s ease-out, opacity 1s ease-out',
                  filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.6))',
                }}
                dangerouslySetInnerHTML={{ __html: SKULL_SVG }}
              />
            </div>
          ))}

          {/* Name and damage */}
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-white font-bold text-base truncate mr-2">
              {combatant.name}
            </span>
            <span className="text-gray-400 text-xs whitespace-nowrap">
              Danno: {combatant.damage}
            </span>
          </div>

          {/* HP bar */}
          <div className="relative h-5 bg-gray-800 rounded-full overflow-hidden border border-gray-600">
            <div
              className="h-full rounded-full"
              style={{
                width: `${hpPercent}%`,
                backgroundColor: barColor,
                transition: 'width 0.5s ease-out',
                boxShadow: `0 0 8px ${barColor}40`,
              }}
            />
            <span className="absolute inset-0 flex items-center justify-center text-white font-bold text-xs" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>
              {combatant.currentHP} / {combatant.maxHP}
            </span>
          </div>
        </div>
      );
    },
    [shakingIds, glowingIds, floatingTexts, deathSkulls]
  );

  return (
    <div className="min-h-screen bg-black p-4 relative overflow-hidden">
      <style>{`
        @keyframes battle-shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .battle-shake {
          animation: battle-shake 0.5s ease-in-out;
        }
        @keyframes battle-glow-anim {
          0% { box-shadow: 0 0 0px rgba(74,222,128,0); }
          50% { box-shadow: 0 0 16px rgba(74,222,128,0.6), 0 0 32px rgba(74,222,128,0.3); }
          100% { box-shadow: 0 0 0px rgba(74,222,128,0); }
        }
        .battle-glow {
          animation: battle-glow-anim 0.8s ease-in-out;
        }
        @keyframes confetti-fall {
          0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0.6; }
        }
        @keyframes outcome-text-pop {
          0% { transform: scale(0.3); opacity: 0; }
          60% { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes defeat-pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @keyframes swords-cross {
          0% { transform: scale(0.5) rotate(0deg); opacity: 0; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
      `}</style>

      {/* Heroes section */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white mb-3 text-center" style={{ textShadow: '0 0 12px rgba(59,130,246,0.5)' }}>
          EROI
        </h2>
        {heroes.map(renderCombatant)}
      </div>

      {/* Divider */}
      <div className="h-px bg-gray-700 mb-6" />

      {/* Enemies section */}
      <div>
        <h2 className="text-xl font-bold text-white mb-3 text-center" style={{ textShadow: '0 0 12px rgba(167,139,250,0.5)' }}>
          NEMICI
        </h2>
        {enemies.map(renderCombatant)}
      </div>

      {/* Battle effects overlay */}
      <BattleEffectsOverlay socket={getAudienceSocket()} />

      {/* Outcome overlay */}
      {outcome && (
        <div
          className="absolute inset-0 flex items-center justify-center z-50 pointer-events-none"
          style={{
            backgroundColor: outcome === 'defeat' ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)',
            opacity: outcomeVisible ? 1 : 0,
            transition: 'opacity 0.5s ease',
          }}
        >
          {outcome === 'victory' && (
            <>
              {Array.from({ length: 25 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: '-10px',
                    width: `${6 + Math.random() * 8}px`,
                    height: `${6 + Math.random() * 8}px`,
                    backgroundColor: ['#fbbf24', '#ef4444', '#3b82f6', '#22c55e', '#a855f7', '#f97316', '#ec4899'][i % 7],
                    borderRadius: Math.random() > 0.5 ? '50%' : '2px',
                    animation: `confetti-fall ${2.5 + Math.random() * 2}s linear ${Math.random() * 1.5}s infinite`,
                  }}
                />
              ))}
              <div className="text-center" style={{ animation: 'outcome-text-pop 0.8s ease-out forwards' }}>
                <div className="text-5xl font-bold text-yellow-400 mb-2" style={{ textShadow: '0 0 30px rgba(251,191,36,0.6), 0 4px 8px rgba(0,0,0,0.8)' }}>
                  VITTORIA!
                </div>
                <div className="text-xl text-yellow-200" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                  Gli eroi trionfano!
                </div>
              </div>
            </>
          )}

          {outcome === 'defeat' && (
            <>
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute rounded-full"
                  style={{
                    left: `${10 + Math.random() * 80}%`,
                    top: `${10 + Math.random() * 80}%`,
                    width: `${40 + Math.random() * 60}px`,
                    height: `${40 + Math.random() * 60}px`,
                    background: 'radial-gradient(circle, rgba(127,29,29,0.3) 0%, transparent 70%)',
                    animation: `defeat-pulse ${2 + Math.random() * 2}s ease-in-out ${Math.random()}s infinite`,
                  }}
                />
              ))}
              <div className="text-center" style={{ animation: 'outcome-text-pop 0.8s ease-out forwards' }}>
                <div
                  className="mx-auto mb-4"
                  style={{ width: '80px', height: '80px', filter: 'drop-shadow(0 0 16px rgba(239,68,68,0.5))' }}
                  dangerouslySetInnerHTML={{ __html: SKULL_SVG }}
                />
                <div className="text-5xl font-bold text-red-500" style={{ textShadow: '0 0 30px rgba(239,68,68,0.5), 0 4px 8px rgba(0,0,0,0.8)' }}>
                  SCONFITTA
                </div>
                <div className="text-xl text-red-300 mt-2" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                  Gli eroi sono stati sconfitti...
                </div>
              </div>
            </>
          )}

          {outcome === 'draw' && (
            <div className="text-center" style={{ animation: 'outcome-text-pop 0.8s ease-out forwards' }}>
              <div className="text-5xl mb-4" style={{ animation: 'swords-cross 0.8s ease-out forwards' }}>
                ⚔️
              </div>
              <div className="text-5xl font-bold text-gray-300" style={{ textShadow: '0 0 30px rgba(156,163,175,0.4), 0 4px 8px rgba(0,0,0,0.8)' }}>
                PAREGGIO
              </div>
              <div className="text-xl text-gray-400 mt-2" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                La battaglia non ha un vincitore
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
